import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, User, Clock, Search } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { motion } from 'framer-motion';
import { CTASection } from '../components/sections/CTASection';
import { useBlogPosts } from '../hooks/useBlogPosts';
const categoryColors: Record<string, string> = {
  Strategy: 'bg-[#0D9488] text-white',
  Design: 'bg-[#FF6600] text-white',
  Development: 'bg-[#00316B] text-white',
  Branding: 'bg-[#7C3AED] text-white'
};
const CATEGORIES = ['All', 'Strategy', 'Design', 'Development', 'Branding'];
export function BlogPage() {
  const {
    posts
  } = useBlogPosts();
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');
  const filtered = posts.filter(post => {
    const matchesCategory = activeCategory === 'All' || post.category === activeCategory;
    const matchesSearch = post.title.toLowerCase().includes(search.toLowerCase()) || post.excerpt.toLowerCase().includes(search.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  return <main className="bg-[#F8F8F6] min-h-screen pt-24" data-id="element-472">
      {/* Header */}
      <section className="bg-[#00316B] text-white py-20 relative overflow-hidden" data-id="element-473">
        <div className="absolute inset-0 opacity-10" data-id="element-474">
          <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-475">
            <path d="M0 100 C 20 0 50 0 100 100 Z" fill="white" data-id="element-476" />
          </svg>
        </div>
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-477">
          <div className="max-w-3xl" data-id="element-478">
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="text-4xl md:text-6xl font-bold mb-6 font-heading" data-id="element-479">
              Our Blog
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-xl text-blue-100 leading-relaxed" data-id="element-480">
              Insights, strategies, and stories from the forefront of digital
              innovation.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Filter Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-20 z-30 shadow-sm" data-id="element-481">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-482">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-483">
            <div className="flex items-center space-x-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0" data-id="element-484">
              {CATEGORIES.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${activeCategory === cat ? 'bg-[#00316B] text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-485">
                  {cat}
                </button>)}
            </div>
            <div className="relative w-full md:w-64" data-id="element-486">
              <input type="text" placeholder="Search articles..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6600] focus:border-transparent" data-id="element-487" />
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" data-id="element-488" />
            </div>
          </div>
        </div>
      </div>

      {/* Blog Grid */}
      <section className="py-16" data-id="element-489">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-490">
          {filtered.length === 0 ? <div className="text-center py-20 text-gray-500" data-id="element-491">
              No articles found.
            </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-492">
              {filtered.map((post, index) => <motion.div key={post.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.05
          }} data-id="element-493">
                  <Link to={`/blog/${post.slug}`} className="block h-full" data-id="element-494">
                    <Card className="h-full flex flex-col p-0 overflow-hidden group cursor-pointer border-gray-100 hover:border-blue-100 transition-colors" data-id="element-495">
                      <div className="relative h-56 overflow-hidden" data-id="element-496">
                        <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-497" />
                        <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-semibold ${categoryColors[post.category] ?? 'bg-gray-200 text-gray-800'}`} data-id="element-498">
                          {post.category}
                        </div>
                      </div>
                      <div className="p-6 flex flex-col flex-1" data-id="element-499">
                        <div className="flex items-center text-xs text-gray-500 mb-4 space-x-4" data-id="element-500">
                          <span className="flex items-center" data-id="element-501">
                            <Calendar className="w-3 h-3 mr-1" data-id="element-502" />
                            {post.date}
                          </span>
                          <span className="flex items-center" data-id="element-503">
                            <Clock className="w-3 h-3 mr-1" data-id="element-504" />
                            {post.readTime}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-[#0C0C0C] mb-3 group-hover:text-[#00316B] transition-colors line-clamp-2" data-id="element-505">
                          {post.title}
                        </h3>
                        <p className="text-gray-600 text-sm mb-6 line-clamp-3 flex-1" data-id="element-506">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center text-[#FF6600] font-medium text-sm mt-auto group-hover:underline" data-id="element-507">
                          Read Article{' '}
                          <ArrowRight className="ml-2 w-3 h-3 transition-transform group-hover:translate-x-1" data-id="element-508" />
                        </div>
                      </div>
                    </Card>
                  </Link>
                </motion.div>)}
            </div>}
        </div>
      </section>

      <CTASection data-id="element-509" />
    </main>;
}